import React, { useRef, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { getPointerColor } from '../../utils/pointerUtils';

export const TreeRenderer = ({ tree, rootId, memory, highlightIndices = [], isLight, textColor }) => {
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(800);

  // Helper to safely get numeric coordinates
  const safeCoord = (val, fallback = 0) => {
    const num = Number(val);
    return isNaN(num) ? fallback : num;
  };

  useEffect(() => {
    if (containerRef.current) {
      const updateWidth = () => {
        if (containerRef.current) {
          setContainerWidth(containerRef.current.offsetWidth);
        }
      };
      updateWidth();
      window.addEventListener('resize', updateWidth);
      return () => window.removeEventListener('resize', updateWidth);
    }
  }, []);

  if (!tree || !rootId) return null;

  // Layout algorithm: Basic level-order to assign (x, y) coordinates
  const positions = {};
  const levelHeight = 70;

  const calculatePositions = (id, depth, left, right) => {
    const node = tree[String(id)];
    if (!node) return;

    const x = (left + right) / 2;
    const y = depth * levelHeight + 45; // Offset for pointers
    positions[String(id)] = { x, y };

    if (node.left) calculatePositions(node.left, depth + 1, left, x);
    if (node.right) calculatePositions(node.right, depth + 1, x, right);
  };

  // Adjust coordinates based on available container width
  calculatePositions(rootId, 0, 40, containerWidth - 40);

  const nodeEntries = Object.entries(tree);

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%', height: '260px', overflow: 'hidden', background: isLight ? '#f8fafc40' : '#1e1e2240', borderRadius: '12px', border: `1px dashed ${isLight ? '#e2e8f0' : '#27272a'}` }}>
      <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
        <defs>
          <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="0" refY="3.5" orient="auto">
            <polygon points="0 0, 10 3.5, 0 7" fill={isLight ? '#cbd5e1' : '#3f3f46'} />
          </marker>
        </defs>
        {nodeEntries.map(([id, node]) => {
          const pos = positions[id];
          if (!pos) return null;

          const fx = safeCoord(pos.x);
          const fy = safeCoord(pos.y);

          const isHighlighted = highlightIndices.map(String).includes(String(id));

          return (
            <React.Fragment key={`edges-${id}`}>
              {node.left && positions[String(node.left)] && (
                <motion.line
                  x1={fx} y1={fy}
                  x2={safeCoord(positions[String(node.left)].x)} y2={safeCoord(positions[String(node.left)].y)}
                  stroke={isHighlighted ? '#3b82f6' : (isLight ? '#cbd5e1' : '#3f3f46')}
                  strokeWidth={isHighlighted ? "3" : "2"}
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                />
              )}
              {node.right && positions[String(node.right)] && (
                <motion.line
                  x1={fx} y1={fy}
                  x2={safeCoord(positions[String(node.right)].x)} y2={safeCoord(positions[String(node.right)].y)}
                  stroke={isHighlighted ? '#3b82f6' : (isLight ? '#cbd5e1' : '#3f3f46')}
                  strokeWidth={isHighlighted ? "3" : "2"}
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                />
              )}
            </React.Fragment>
          );
        })}
      </svg>

      {nodeEntries.map(([id, node]) => {
        const pos = positions[id];
        if (!pos) return null;
        const px = safeCoord(pos.x);
        const py = safeCoord(pos.y);

        const pointers = Object.entries(memory)
          .filter(([_, v]) => String(v) === id)
          .map(([k]) => k);

        const isHighlighted = highlightIndices.map(String).includes(String(id));

        return (
          <div
            key={id}
            style={{
              position: 'absolute',
              left: px,
              top: py,
              transform: 'translate(-50%, -50%)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              zIndex: 10
            }}
          >
            {/* Pointers above node - adjusted for vertical space */}
            <div style={{ position: 'absolute', bottom: '110%', display: 'flex', gap: '2px', flexWrap: 'wrap', justifyContent: 'center' }}>
              {pointers.map(ptr => {
                const pc = getPointerColor(ptr);
                return (
                  <motion.div
                    key={ptr}
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    style={{
                      fontSize: '10px',
                      fontWeight: 900,
                      color: pc.text,
                      background: pc.bg,
                      padding: '1px 6px',
                      borderRadius: '4px',
                      border: `1px solid ${pc.border}`,
                      whiteSpace: 'nowrap',
                      boxShadow: `0 2px 4px rgba(0,0,0,0.1)`
                    }}
                  >
                    {ptr}
                  </motion.div>
                );
              })}
            </div>

            <motion.div
              layoutId={`node-${id}`}
              animate={{
                scale: isHighlighted ? 1.15 : 1,
                borderColor: isHighlighted ? '#3b82f6' : (pointers.length > 0 ? (isLight ? '#3b82f6' : '#2563eb') : (isLight ? '#cbd5e1' : '#3f3f46')),
                boxShadow: isHighlighted
                  ? `0 0 20px rgba(59, 130, 246, 0.6)`
                  : (pointers.length > 0 ? `0 0 15px ${getPointerColor(pointers[0]).border}60` : '0 4px 6px -1px rgb(0 0 0 / 0.1)')
              }}
              style={{
                minWidth: '44px',
                height: '44px',
                borderRadius: '22px',
                padding: '0 12px',
                border: '2px solid',
                background: isLight ? '#fff' : '#18181b',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '13px',
                fontWeight: 800,
                color: textColor,
                position: 'relative',
                whiteSpace: 'nowrap'
              }}
            >
              {isHighlighted && (
                <motion.div
                  animate={{ scale: [1, 1.4, 1], opacity: [0.3, 0, 0.3] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                  style={{
                    position: 'absolute',
                    top: -4, left: -4, right: -4, bottom: -4,
                    borderRadius: '50%',
                    border: '2px solid #3b82f6',
                    pointerEvents: 'none'
                  }}
                />
              )}
              {node.val}
            </motion.div>
          </div>
        );
      })}
    </div>
  );
};
